import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { shortenAddress } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

export function Header() {
  const { publicKey, connected } = useWallet();
  const { setVisible } = useWalletModal();
  const { isAuthenticated, isLoading, authenticate, logout } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authAttempted, setAuthAttempted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Reset auth attempted flag when wallet changes
  useEffect(() => {
    setAuthAttempted(false);
  }, [publicKey?.toString()]);

  // Auto-authenticate once when wallet connects (don't retry on failure)
  useEffect(() => {
    if (connected && publicKey && !isAuthenticated && !isLoading && !isAuthenticating && !authAttempted) {
      handleAuthenticate();
    }
  }, [connected, publicKey, isAuthenticated, isLoading, isAuthenticating, authAttempted]);

  const handleAuthenticate = async () => {
    setIsAuthenticating(true);
    setAuthAttempted(true);
    try {
      await authenticate();
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleWalletClick = async () => {
    if (connected) {
      console.log('Disconnecting wallet...');
      try {
        await logout();
        console.log('Disconnected');
      } catch (err) {
        console.error('Disconnect failed:', err);
      }
    } else {
      setVisible(true);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 12px',
      borderBottom: '1px solid var(--border)',
      background: 'var(--bg-primary)',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      <Link to="/" style={{
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <div style={{
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <SkopLogo />
        </div>
        <span style={{
          fontSize: '15px',
          fontWeight: 600,
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-primary)',
          letterSpacing: '0.05em',
        }}>
          SKōP
        </span>
      </Link>

      {/* Desktop Nav */}
      <nav className="desktop-nav" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
      }}>
        {connected && isAuthenticated && (
          <>
            <NavLink to="/portfolio" active={isActive('/portfolio')} title="Portfolio">
              <WalletIcon />
            </NavLink>
            <NavLink to="/alerts" active={isActive('/alerts')} title="Alerts">
              <BellIcon />
            </NavLink>
            <NavLink to="/whales" active={isActive('/whales')} title="Whales">
              <ChartIcon />
            </NavLink>
          </>
        )}

        {connected && !isAuthenticated && !isAuthenticating && (
          <button
            onClick={handleAuthenticate}
            style={{
              padding: '6px 12px',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: '#000',
              fontSize: '12px',
              fontFamily: 'var(--font-mono)',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            SIGN
          </button>
        )}

        {(isAuthenticating || isLoading) && connected && (
          <span style={{
            fontSize: '11px',
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
          }}>
            ...
          </span>
        )}

        <button
          onClick={handleWalletClick}
          title={connected ? 'Click to disconnect' : 'Connect wallet'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            marginLeft: '6px',
            fontSize: '12px',
            fontFamily: 'var(--font-mono)',
            fontWeight: 500,
            background: connected ? 'transparent' : 'var(--accent)',
            color: connected ? 'var(--text-primary)' : '#000',
            border: '1px solid',
            borderColor: connected ? 'var(--border)' : 'var(--accent)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => connected && (e.currentTarget.style.opacity = '0.7')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          {connected && publicKey ? (
            <>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: 'var(--success)',
              }} />
              {shortenAddress(publicKey.toString())}
            </>
          ) : (
            'CONNECT'
          )}
        </button>
      </nav>

      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        style={{
          display: 'none',
          padding: '8px',
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
        }}
      >
        <MenuIcon open={mobileMenuOpen} />
      </button>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'var(--bg-primary)',
            borderBottom: '1px solid var(--border)',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {connected && isAuthenticated && (
            <>
              <MobileNavLink to="/portfolio" active={isActive('/portfolio')} onClick={() => setMobileMenuOpen(false)}>
                <WalletIcon /> Portfolio
              </MobileNavLink>
              <MobileNavLink to="/alerts" active={isActive('/alerts')} onClick={() => setMobileMenuOpen(false)}>
                <BellIcon /> Alerts
              </MobileNavLink>
              <MobileNavLink to="/whales" active={isActive('/whales')} onClick={() => setMobileMenuOpen(false)}>
                <ChartIcon /> Whale Tracker
              </MobileNavLink>
            </>
          )}
          <button
            onClick={() => { handleWalletClick(); setMobileMenuOpen(false); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              background: connected ? 'transparent' : 'var(--accent)',
              color: connected ? 'var(--text-primary)' : '#000',
              border: '1px solid',
              borderColor: connected ? 'var(--border)' : 'var(--accent)',
              borderRadius: 'var(--radius-md)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {connected && publicKey ? (
              <>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--success)' }} />
                {shortenAddress(publicKey.toString())} (Disconnect)
              </>
            ) : (
              'Connect Wallet'
            )}
          </button>
        </div>
      )}

    </header>
  );
}

function NavLink({
  to,
  active,
  title,
  children
}: {
  to: string;
  active: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        borderRadius: 'var(--radius-md)',
        color: active ? 'var(--accent)' : 'var(--text-tertiary)',
        background: active ? 'var(--accent-muted)' : 'transparent',
        textDecoration: 'none',
        transition: 'all 0.1s',
      }}
    >
      {children}
    </Link>
  );
}

function SkopLogo() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
      <circle cx="12" cy="12" r="9" stroke="var(--accent)" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="5" stroke="var(--accent)" strokeWidth="1" opacity="0.5" />
      <circle cx="12" cy="12" r="2" fill="var(--accent)" />
    </svg>
  );
}

function WalletIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3v18h18"/>
      <path d="M18 17V9"/>
      <path d="M13 17V5"/>
      <path d="M8 17v-3"/>
    </svg>
  );
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {open ? (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      )}
    </svg>
  );
}

function MobileNavLink({
  to,
  active,
  onClick,
  children
}: {
  to: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        color: active ? 'var(--accent)' : 'var(--text-secondary)',
        background: active ? 'var(--accent-muted)' : 'var(--surface)',
        border: '1px solid var(--border)',
        textDecoration: 'none',
        fontSize: '13px',
      }}
    >
      {children}
    </Link>
  );
}

